import { DocumentNode, OperationVariables } from '@apollo/client';
import { useQuery } from "@apollo/client/react";
import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { getLocalStorageToken } from 'store/localStorage'
import paginate from 'utils/paginate'
import { useScrollEdges } from 'hooks/useScrollEdges'
export interface IColInfo {
  key: string
  titleTranslationKey?: string
  sortable: boolean
  /** Hidden below the `sm` breakpoint. */
  hideOnMobile?: boolean
  /** In px. The cell's content wraps within it. */
  maxWidth?: number
  /** In px. Keeps e.g. a date on one line. */
  minWidth?: number
  /** Not implemented by GraphQLTable. */
  layoutWeight?: number
  sortOrder?: 'ASC' | 'DESC' | 'NONE'
  actionColumn?: boolean
}
export interface TableRowData {
  id: number | string
  onClick?: () => void
  colData: {
    [key: string]: string | JSX.Element | number | { title: string; action: () => void }[]
  }
  className?: string
}
interface DataExtractorReturnType {
  nrOfPages: number
  rows: TableRowData[]
}
export type DataExtractor<TQuery = any> = (data: TQuery | undefined) => DataExtractorReturnType

// Tables sit in dashboard and side cards narrower than their content used to
// be (every cell px-6 + no wrapping), so columns were cut off at the card
// edge (TODO T6.20). px-3 at every size (the cards stay narrow on desktop),
// text wraps, and the width options the callers already declared are applied.
const cellPadding = 'px-3'
const hideClass = (col: IColInfo) => (col.hideOnMobile ? 'hidden sm:table-cell' : '')
// max/min-width are unreliable on table cells themselves; on an inner box they work everywhere.
// maxWidth caps the column's content, not its header: a one-word uppercase
// header ("ORGANISATIONER") can't wrap and would spill past the cap.
const CellBox: React.FC<{ col: IColInfo; header?: boolean; children: React.ReactNode }> = ({
  col,
  header,
  children,
}) => {
  const maxWidth = header ? undefined : col.maxWidth
  return maxWidth || col.minWidth ? (
    <div style={{ maxWidth, minWidth: col.minWidth }}>{children}</div>
  ) : (
    <>{children}</>
  )
}

export interface Props<TQuery, TQueryVariables> {
  query: DocumentNode
  queryVariables: TQueryVariables
  colInfo: IColInfo[]
  extractData: DataExtractor<TQuery>
  pageSize: number
  skip?: boolean
  hideGoToLast?: boolean
  grow?: boolean
  tableHeaderRow?: TableRowData
  noLoading?: boolean
  pollInterval?: number
  fetchCacheAndNetwork?: boolean
}
function GraphQLTable<TQuery = any, TQueryVariables extends OperationVariables = any>({
  query,
  queryVariables,
  extractData,
  colInfo,
  pageSize,
  skip,
  pollInterval,
  fetchCacheAndNetwork,
}: Props<TQuery, TQueryVariables>) {
  const [paginationVariables, setPaginationVariables] = useState({ first: 0, after: pageSize })
  const { data, loading, error } = useQuery<TQuery, TQueryVariables>(query, {
    variables: { ...queryVariables, token: getLocalStorageToken(), ...paginationVariables },
    skip,
    pollInterval,
    fetchPolicy: fetchCacheAndNetwork ? 'cache-and-network' : 'cache-first',
  })

  const handlePageClick = useCallback(
    (pageNr: number) => {
      setPaginationVariables({ first: (pageNr - 1) * pageSize, after: paginationVariables.after })
    },
    [pageSize, paginationVariables.after],
  )
  const { rows, nrOfPages } = extractData(data)
  const rowCount = rows.length
  const { pages, currentPage } = useMemo(
    () =>
      paginate(
        pageSize * nrOfPages,
        Math.floor(paginationVariables.first / pageSize) + 1,
        pageSize,
        4,
      ),
    [pageSize, nrOfPages, paginationVariables.first],
  )

  // Auto-navigate to previous page when current page becomes empty
  const didAutoPageBack = useRef(false)
  useEffect(() => {
    const currPageFromFirst = Math.ceil((paginationVariables.first + 1) / paginationVariables.after)
    if (!rowCount && currPageFromFirst > 1 && !loading && nrOfPages > 0) {
      if (!didAutoPageBack.current) {
        didAutoPageBack.current = true
        handlePageClick(currPageFromFirst - 1)
      }
    } else {
      didAutoPageBack.current = false
    }
  }, [
    rowCount,
    handlePageClick,
    loading,
    nrOfPages,
    paginationVariables.first,
    paginationVariables.after,
  ])
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const edges = useScrollEdges(scrollRef)
  const inactivePageClass =
    'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 border-t-2 pt-4 px-2 @xs:px-4 inline-flex items-center text-sm font-medium cursor-pointer select-none'
  const activePageClass =
    'border-primary-500 text-primary-600 border-t-2 pt-4 px-2 @xs:px-4 inline-flex items-center text-sm font-medium cursor-pointer select-none'

  return (
    <div className="flex flex-col">
      {error && !data ? (
        <div className="rounded-sm bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {t('general.loadingError')}
        </div>
      ) : null}
      <div className="relative">
      <div ref={scrollRef} className="-my-2 overflow-x-auto">
        <div className="py-2 align-middle inline-block min-w-full">
          <div className="overflow-hidden sm:rounded-lg">
            <table className="min-w-full ">
              <thead className="">
                <tr>
                  {colInfo.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={`${hideClass(col)} ${cellPadding} py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      <CellBox col={col} header>{col.titleTranslationKey && t(col.titleTranslationKey)}</CellBox>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows?.map((row, i) => (
                  <tr
                    onClick={row.onClick}
                    key={row.id}
                    className={`hover:bg-gray-100 bg-white ${row.onClick ? 'cursor-pointer' : ''} ${
                      row.className ? row.className : ''
                    }`}
                  >
                    {colInfo.map((col) => (
                      <td
                        key={col.key}
                        className={`${hideClass(col)} ${cellPadding} py-3 lg:py-4 break-words text-xs text-gray-500`}
                      >
                        <CellBox col={col}>
                          {!col.actionColumn ? (row.colData[col.key] as React.ReactNode) : null}
                        </CellBox>
                      </td>
                    ))}
                  </tr>
                ))}
                {typeof pageSize === 'number' && typeof rows.length === 'number'
                  ? Array(Math.max(pageSize - rows.length, 0))
                      .fill(4)
                      .map((_, index) => (
                        <tr key={index} className={'hover:bg-gray-100 bg-white'}>
                          {colInfo.map((col) => (
                            <td
                              key={col.key}
                              className={`${
                                !loading ? 'text-transparent' : ''
                              } ${hideClass(col)} ${cellPadding} py-3 lg:py-4 whitespace-nowrap text-xs`}
                            >
                              {loading ? <Skeleton /> : '.'}
                            </td>
                          ))}
                        </tr>
                      ))
                  : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* A fade on each edge that hides columns: the table scrolls sideways there. */}
      {edges.left ? (
        <div
          aria-hidden="true"
          data-testid="scroll-cue-left"
          className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r from-white to-transparent"
        />
      ) : null}
      {edges.right ? (
        <div
          aria-hidden="true"
          data-testid="scroll-cue-right"
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-white to-transparent"
        />
      ) : null}
      </div>
      {/* @container: the pager adapts to this table's width, not the viewport's —
          tables sit in cards that stay narrow on wide screens. Labels from 20rem
          (320px; a dashboard card on a 1440px screen is ~386px), arrows only below,
          where they made the page numbers wrap onto a second line.
          Only around the pager: a container has no intrinsic width, so on the
          table's root it collapsed anything sized to the table — the person
          timestamps dialog shrank to 20 px. Here it takes the table's width. */}
      {!loading && nrOfPages > 1 && (
        <div className="@container">
        <nav className="border-t border-gray-200 px-4 flex items-center justify-between sm:px-0">
          <div className="-mt-px flex shrink-0">
            <button type="button"
              onClick={() => handlePageClick(currentPage - 1)}
              disabled={currentPage === 1 || nrOfPages === 0}
              className={`hover:text-gray-700 hover:border-gray-300 disabled:text-gray-300
       border-t-2 border-transparent pt-4 pl-1 inline-flex items-center text-sm font-medium text-gray-500 `}
            >
              <svg
                className="mr-1 @xs:mr-3 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="hidden @xs:inline">{t('general.previous')}</span>
            </button>
          </div>
          <div className="-mt-px flex flex-wrap justify-center min-w-0">
            {!pages.includes(1) && pages.length !== 0 && (
              <span className="border-transparent text-gray-500 border-t-2 pt-4 px-2 @xs:px-4 inline-flex items-center text-sm font-medium">
                ...
              </span>
            )}
            {pages.map((page) => (
              <span
                onClick={() => handlePageClick(page)}
                key={page}
                className={page === currentPage ? activePageClass : inactivePageClass}
              >
                {page}
              </span>
            ))}
            {!pages.includes(nrOfPages) && pages.length !== 0 && (
              <span className="border-transparent text-gray-500 border-t-2 pt-4 px-2 @xs:px-4 inline-flex items-center text-sm font-medium">
                ...
              </span>
            )}
          </div>
          <div className="-mt-px flex shrink-0 justify-end">
            <button type="button"
              onClick={() => handlePageClick(currentPage + 1)}
              disabled={currentPage === nrOfPages || nrOfPages === 0}
              className={`hover:text-gray-700 hover:border-gray-300 disabled:text-gray-300
       border-t-2 border-transparent pt-4 pl-1 inline-flex items-center text-sm font-medium text-gray-500 `}
            >
              <span className="hidden @xs:inline">{t('general.next')}</span>
              <svg
                className="ml-1 @xs:ml-3 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </nav>
        </div>
      )}
    </div>
  )
}

export default GraphQLTable
